import { Controller } from "@hotwired/stimulus"

// Tracks the single currently-open dropdown so opening one closes any other,
// regardless of event propagation (some triggers stopPropagation).
let openDropdown = null

export default class extends Controller {
  static targets = [ "content", "icon" ]

  connect() {
    this.onDocumentClick = this.handleDocumentClick.bind(this)
    this.onKeydown = this.handleKeydown.bind(this)
  }

  disconnect() {
    if (openDropdown === this) openDropdown = null
    this.stopListening()
  }

  toggle(event) {
    if (this.contentTarget.classList.contains("hidden")) {
      this.open(event)
    } else {
      this.close()
    }
  }

  open(event) {
    if (openDropdown && openDropdown !== this) openDropdown.close()
    openDropdown = this

    this.contentTarget.classList.remove("hidden")
    this.positionDropdown(event)
    if (this.hasIconTarget) this.iconTarget.classList.add("rotate-180")

    // Defer attaching so the click that opened the menu doesn't immediately
    // bubble up and close it again.
    requestAnimationFrame(() => {
      document.addEventListener("click", this.onDocumentClick)
      document.addEventListener("keydown", this.onKeydown)
    })
  }

  close() {
    if (this.contentTarget.classList.contains("hidden")) return
    if (openDropdown === this) openDropdown = null
    this.contentTarget.classList.add("hidden")
    this.resetDropdownPosition()
    if (this.hasIconTarget) this.iconTarget.classList.remove("rotate-180")
    this.stopListening()
  }

  stopListening() {
    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("keydown", this.onKeydown)
  }

  handleDocumentClick(event) {
    // Click outside the trigger + menu: dismiss.
    if (!this.element.contains(event.target)) {
      this.close()
      return
    }
    // Click on an item inside the menu (link/button): dismiss after selection.
    if (this.contentTarget.contains(event.target) && event.target.closest("a, button")) {
      this.close()
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  positionDropdown(event) {
    this.resetDropdownPosition();
    requestAnimationFrame(() => {
      const trigger = event?.currentTarget || this.element;
      const triggerRect = trigger.getBoundingClientRect();
      const menuRect = this.contentTarget.getBoundingClientRect();
      const padding = 8;

      let top = triggerRect.bottom + 8;
      const maxBottom = window.innerHeight - padding;
      if (top + menuRect.height > maxBottom) {
        top = triggerRect.top - menuRect.height - 8;
      }
      top = Math.max(padding, Math.min(top, maxBottom - menuRect.height));

      let left = triggerRect.right - menuRect.width;
      left = Math.max(padding, Math.min(left, window.innerWidth - menuRect.width - padding));

      this.contentTarget.style.position = "fixed";
      this.contentTarget.style.top = `${top}px`;
      this.contentTarget.style.left = `${left}px`;
      this.contentTarget.style.right = "auto";
      this.contentTarget.style.marginTop = "0";
    });
  }

  resetDropdownPosition() {
    this.contentTarget.style.position = "";
    this.contentTarget.style.top = "";
    this.contentTarget.style.left = "";
    this.contentTarget.style.right = "";
    this.contentTarget.style.marginTop = "";
  }
}
